/**
 * frontend/src/lib/authApi.js
 * Production-ready fetch helpers for backend-managed JWT auth.
 *
 * Assumptions:
 * - Browser calls your backend through /api/* (Caddy strips /api upstream)
 * - Backend expects Authorization: Bearer <JWT>
 */

export const TOKEN_KEY = "api_token";
export const USER_KEY = "api_user";

export const API_BASE =
  (typeof process !== "undefined" &&
    process?.env &&
    process.env.REACT_APP_API_BASE) ||
  "/api";

// ───────────────────────────────────────────────────────────────────────────────
// Auth storage
// ───────────────────────────────────────────────────────────────────────────────
export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setAuth(token, user) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      if (user.role) localStorage.setItem("role", user.role);
      if (user.org_id) localStorage.setItem("org_id", user.org_id);
    }
  } catch {}
}

export function clearAuth() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem("role");
    localStorage.removeItem("org_id");
  } catch {}
}

// ───────────────────────────────────────────────────────────────────────────────
// Response normalization
// ───────────────────────────────────────────────────────────────────────────────
export function unwrapRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.patients)) return payload.patients;
  if (Array.isArray(payload.providers)) return payload.providers;
  if (Array.isArray(payload.facilities)) return payload.facilities;
  for (const v of Object.values(payload)) {
    if (Array.isArray(v)) return v;
  }
  return [];
}

// ───────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ───────────────────────────────────────────────────────────────────────────────
function isAbsoluteUrl(url) {
  return /^https?:\/\//i.test(url);
}

function withBase(url) {
  if (isAbsoluteUrl(url)) return url;
  if (url.startsWith("/api/")) return url; // allow callers who already include /api
  if (url.startsWith("/")) return `${API_BASE}${url}`;
  return `${API_BASE}/${url}`;
}

function authHeaders(extra = {}) {
  const token = getToken();
  return {
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function parseResponse(res) {
  if (res.status === 401) clearAuth();

  const contentType = res.headers.get("content-type") || "";
  const text = await res.text().catch(() => "");

  if (!res.ok) {
    try {
      const j = JSON.parse(text);
      throw new Error(j?.error || j?.message || text || `HTTP ${res.status}`);
    } catch {
      throw new Error(text || `HTTP ${res.status}`);
    }
  }

  if (contentType.includes("application/json")) {
    return text ? JSON.parse(text) : {};
  }

  return text;
}

// ───────────────────────────────────────────────────────────────────────────────
// Auth endpoints (optional convenience)
// ───────────────────────────────────────────────────────────────────────────────
export async function login(email, password) {
  const res = await fetch(withBase("/auth/login"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });

  const data = await parseResponse(res);
  if (data?.token) setAuth(data.token, data.user || null);
  return data;
}

export async function logout() {
  clearAuth();
}

// ───────────────────────────────────────────────────────────────────────────────
// Fetch helpers
// ───────────────────────────────────────────────────────────────────────────────
export async function authGet(url) {
  const res = await fetch(withBase(url), { headers: authHeaders() });
  return await parseResponse(res);
}

export async function authPost(url, body) {
  const res = await fetch(withBase(url), {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body ?? {}),
  });
  return await parseResponse(res);
}

export async function authPut(url, body) {
  const res = await fetch(withBase(url), {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body ?? {}),
  });
  return await parseResponse(res);
}

export async function authPatch(url, body) {
  const res = await fetch(withBase(url), {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body ?? {}),
  });
  return await parseResponse(res);
}

/**
 * ✅ Updated: allow JSON body (for org_id, etc.)
 */
export async function authDelete(url, body) {
  const hasBody = body !== undefined && body !== null;
  const res = await fetch(withBase(url), {
    method: "DELETE",
    headers: authHeaders(hasBody ? { "Content-Type": "application/json" } : {}),
    body: hasBody ? JSON.stringify(body) : undefined,
  });
  return await parseResponse(res);
}

export async function authPostForm(url, formData) {
  const res = await fetch(withBase(url), {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  });
  return await parseResponse(res);
}

export async function authFetchBlob(url, options = {}) {
  const res = await fetch(withBase(url), {
    ...options,
    headers: authHeaders(options.headers || {}),
  });

  if (res.status === 401) clearAuth();

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(msg || `HTTP ${res.status}`);
  }

  return await res.blob();
};